import { Mail, MapPin, Sparkles, Instagram, Facebook, Phone, Briefcase, Handshake, Twitter } from "lucide-react";

// TikTok icon component (not in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg 
    className={className} 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const quickLinks = [
    { name: "Services", href: "#services" },
    { name: "Pricing", href: "#pricing" },
    { name: "Areas", href: "#areas" },
    { name: "Get Quote", href: "#quote" },
  ];

  const services = [
    "Regular Clean",
    "Detailed Clean",
    "Deep Clean",
    "End of Tenancy",
    "Commercial Cleaning",
  ];

  const areas = [
    "City Centre",
    "Ecclesall",
    "Broomhill",
    "Crookes",
    "Hillsborough",
    "Dore & Totley",
  ];

  const socialLinks = [
    { 
      name: "Instagram", 
      icon: Instagram, 
      href: "https://www.instagram.com/dcssheffield", 
      hoverColor: "hover:text-pink-500",
    },
    { 
      name: "Facebook", 
      icon: Facebook, 
      href: "https://www.facebook.com/share/19UBcrLFKt/", 
      hoverColor: "hover:text-blue-500",
    },
    { 
      name: "TikTok", 
      icon: TikTokIcon, 
      href: "https://www.tiktok.com/@dcssheffield", 
      hoverColor: "hover:text-white",
    },
    { 
      name: "Twitter", 
      icon: Twitter, 
      href: "https://x.com/DcsSheffield", 
      hoverColor: "hover:text-sky-400",
    },
  ];

  return (
    <footer
      id="contact"
      data-testid="footer"
      className="bg-[#0F172A] text-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_0e17f384-87af-4f21-afc9-222d2d083a19/artifacts/el8zbrgy_cb05fdee-8c6a-4b70-bf8c-a5cc72353916.jpeg"
                alt="Detailed Cleaning Services Logo"
                className="h-20 w-20 rounded-xl"
              />
              <div>
                <span 
                  className="font-bold text-lg tracking-tight block"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  Detailed
                </span>
                <span className="text-xs text-[#3B82F6] font-medium uppercase tracking-wider">
                  Cleaning Services
                </span>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              Sheffield's premier cleaning agency. Professional, reliable cleaning with guaranteed satisfaction.
            </p>
            <div className="flex items-center gap-2 text-slate-400">
              <Sparkles className="w-4 h-4 text-[#3B82F6]" />
              <span className="text-sm italic">let an expert do it</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 
              className="font-bold text-lg mb-6"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-slate-400 hover:text-[#3B82F6] transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 
              className="font-bold text-lg mb-6"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Services
            </h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <span className="text-slate-400 text-sm">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Social */}
          <div>
            <h4 
              className="font-bold text-lg mb-6"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Contact Us
            </h4>
            <div className="space-y-3">
              {/* Phone */}
              <a
                href="tel:01145503757"
                className="flex items-center gap-3 text-slate-400 hover:text-[#3B82F6] transition-colors text-sm"
                data-testid="footer-phone"
              >
                <Phone className="w-5 h-5 text-[#3B82F6]" />
                0114 550 3757
              </a>
              
              {/* Info Email */}
              <a
                href="mailto:info@detailedcleaningservices.co.uk"
                className="flex items-center gap-3 text-slate-400 hover:text-[#3B82F6] transition-colors text-sm"
                data-testid="footer-email-info"
              >
                <Mail className="w-5 h-5 text-[#3B82F6]" />
                info@detailedcleaningservices.co.uk
              </a>
              
              {/* Collabs Email */}
              <a
                href="mailto:collabs@detailedcleaningservices.co.uk"
                className="flex items-center gap-3 text-slate-400 hover:text-[#3B82F6] transition-colors text-sm"
                data-testid="footer-email-collabs"
              >
                <Handshake className="w-5 h-5 text-[#3B82F6]" />
                collabs@detailedcleaningservices.co.uk
              </a>
              
              {/* Roles Email */}
              <a
                href="mailto:roles@detailedcleaningservices.co.uk"
                className="flex items-center gap-3 text-slate-400 hover:text-[#3B82F6] transition-colors text-sm"
                data-testid="footer-email-roles"
              >
                <Briefcase className="w-5 h-5 text-[#3B82F6]" />
                roles@detailedcleaningservices.co.uk
              </a>
              
              {/* Location */}
              <div className="flex items-start gap-3 text-slate-400 text-sm pt-1">
                <MapPin className="w-5 h-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                <span>Serving Sheffield: {areas.join(", ")} and more</span>
              </div>
              
              {/* Social Media Links */}
              <div className="pt-4">
                <p className="text-slate-500 text-xs uppercase tracking-wider mb-3">Follow Us</p>
                <div className="flex items-center gap-3">
                  {socialLinks.map((social) => (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 transition-all duration-300 ${social.hoverColor} hover:scale-110`}
                      data-testid={`social-${social.name.toLowerCase()}`}
                      aria-label={`Follow us on ${social.name}`}
                    >
                      <social.icon className="w-5 h-5" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-slate-800">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {currentYear} Detailed Cleaning Services. All rights reserved.
            </p>
            <p className="text-slate-500 text-sm">
              Sheffield's Certified Cleaning Specialist
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
